import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, CheckCircle2, AlertCircle, RefreshCw, Link2,
  Link2Off, Search, Layers, RotateCcw, Zap, X, Check, Info,
} from 'lucide-react';
import { toast } from 'sonner';
import { categoryMappingAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

// ── Animation variants ────────────────────────────────────────────────────────

const fadeUp = {
  hidden:  { opacity: 0, y: 10 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.035, duration: 0.25, ease: 'easeOut' },
  }),
  exit: { opacity: 0, y: -6, transition: { duration: 0.15 } },
};

const stagger = { visible: { transition: { staggerChildren: 0.04 } } };

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, accent, delay = 0 }) {
  return (
    <motion.div
      variants={fadeUp} custom={delay} initial="hidden" animate="visible"
      className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-3"
    >
      <div className={`p-2 rounded-lg ${accent}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <p className="text-xs text-slate-500 font-medium">{label}</p>
        <p className="text-xl font-bold text-slate-900 tabular-nums leading-tight">{value}</p>
      </div>
    </motion.div>
  );
}

function UnmappedRow({ csvCategory, aopCategories, pending, onChange, delay = 0 }) {
  return (
    <motion.div
      variants={fadeUp} custom={delay} layout
      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border transition-all duration-200 ${
        pending
          ? 'bg-primary-50 border-primary-200'
          : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50/50'
      }`}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${pending ? 'bg-primary-400' : 'bg-amber-400'}`} />
        <span className="text-sm text-slate-700 truncate font-medium">{csvCategory}</span>
        <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium flex-shrink-0">CSV</span>
      </div>
      <ArrowRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <Select value={pending || ''} onValueChange={onChange}>
          <SelectTrigger
            className={`w-full text-sm h-8 transition-colors duration-200 ${
              pending
                ? 'border-primary-300 bg-white text-slate-800 focus:border-primary-400'
                : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
            }`}
          >
            <SelectValue placeholder="Select AOP category…" />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {aopCategories.map(cat => (
              <SelectItem key={cat} value={cat} className="text-sm cursor-pointer">
                {cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </motion.div>
  );
}

function MappedRow({ mapping, onDelete, delay = 0 }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      variants={fadeUp} custom={delay} layout
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="flex items-center gap-3 px-4 py-2.5 rounded-lg border border-slate-200
                 bg-white hover:border-emerald-200 hover:bg-emerald-50/30 transition-all duration-200"
    >
      <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
      <span className="flex-1 text-sm text-slate-600 truncate">{mapping.csv_category}</span>
      <ArrowRight className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
      <span className="flex-1 text-sm font-semibold text-emerald-700 truncate">{mapping.aop_category}</span>
      <AnimatePresence>
        {hovered && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.12 }}
            onClick={onDelete}
            className="p-1 rounded hover:bg-red-100 text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
          >
            <X className="w-3.5 h-3.5" />
          </motion.button>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function CategoryMappingPage() {
  const [fiscalYear] = useState(2027);
  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [recalcResult, setRecalcResult]   = useState(null);

  const [unmappedCats, setUnmappedCats]   = useState([]);
  const [aopCategories, setAopCategories] = useState([]);
  const [savedMappings, setSavedMappings] = useState([]);
  const [pendingMap, setPendingMap]       = useState({});

  const [searchUnmapped, setSearchUnmapped] = useState('');
  const [searchMapped,   setSearchMapped]   = useState('');

  // ── Load ─────────────────────────────────────────────────────────────────────

  const loadData = async () => {
    setLoading(true);
    try {
      const [unmRes, mapRes] = await Promise.all([
        categoryMappingAPI.getUnmapped(fiscalYear),
        categoryMappingAPI.getMappings(fiscalYear),
      ]);
      setUnmappedCats(unmRes.data.unmapped_csv_categories || []);
      setAopCategories(unmRes.data.aop_categories || []);
      setSavedMappings(mapRes.data || []);
      setPendingMap({});
    } catch {
      toast.error('Failed to load category data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [fiscalYear]); // eslint-disable-line

  // ── Derived ──────────────────────────────────────────────────────────────────

  const filteredUnmapped = useMemo(() =>
    unmappedCats.filter(c => c.toLowerCase().includes(searchUnmapped.toLowerCase())),
    [unmappedCats, searchUnmapped]
  );

  const filteredMapped = useMemo(() =>
    savedMappings.filter(m =>
      m.csv_category.toLowerCase().includes(searchMapped.toLowerCase()) ||
      m.aop_category.toLowerCase().includes(searchMapped.toLowerCase())
    ),
    [savedMappings, searchMapped]
  );

  const pendingCount = Object.keys(pendingMap).length;

  // ── Actions ──────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!pendingCount) return;
    setSaving(true);
    try {
      const mappings = Object.entries(pendingMap).map(([csv_category, aop_category]) => ({
        csv_category, aop_category,
      }));
      await categoryMappingAPI.saveMappings(mappings, fiscalYear);
      toast.success(`${pendingCount} mapping${pendingCount > 1 ? 's' : ''} saved`);
      await loadData();
    } catch {
      toast.error('Failed to save mappings');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (csvCategory) => {
    try {
      await categoryMappingAPI.deleteMapping(csvCategory, fiscalYear);
      toast.success('Mapping removed');
      await loadData();
    } catch {
      toast.error('Failed to remove mapping');
    }
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    setRecalcResult(null);
    try {
      const res = await categoryMappingAPI.recalculate(fiscalYear);
      setRecalcResult(res.data);
      toast.success(`Actuals rebuilt — ${res.data.channels_rebuilt} channels updated`);
      await loadData();
    } catch {
      toast.error('Recalculate failed');
    } finally {
      setRecalculating(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Category Mapping</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Map imported CSV categories to AOP skeleton categories, then rebuild actuals.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            disabled={loading}
            className="gap-1.5 text-slate-600"
          >
            <RotateCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                size="sm"
                className="gap-1.5 bg-primary-500 hover:bg-primary-600 text-white"
                disabled={recalculating}
              >
                {recalculating
                  ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  : <Zap className="w-3.5 h-3.5" />
                }
                {recalculating ? 'Recalculating…' : 'Recalculate Actuals'}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Rebuild Actuals from AOP Skeleton?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will clear and rebuild <strong>channel_actuals</strong> using the AOP structure
                  as scaffold, applying all saved mappings. All AOP channels and categories will be
                  present; unmapped CSV categories will be excluded.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleRecalculate}
                  className="bg-primary-500 hover:bg-primary-600 text-white"
                >
                  Yes, Recalculate
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* ── Stats ────────────────────────────────────────────────────────── */}
      {!loading && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon={Layers}    label="AOP Categories" value={aopCategories.length}
            accent="bg-blue-100 text-blue-600"   delay={0} />
          <StatCard icon={Link2Off}  label="Unmapped CSV"   value={unmappedCats.length}
            accent={unmappedCats.length > 0 ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-400'} delay={1} />
          <StatCard icon={Link2}     label="Mapped"         value={savedMappings.length}
            accent="bg-emerald-100 text-emerald-600" delay={2} />
          <StatCard icon={Info}      label="Pending Save"   value={pendingCount}
            accent={pendingCount > 0 ? 'bg-primary-100 text-primary-600' : 'bg-slate-100 text-slate-400'} delay={3} />
        </div>
      )}

      {/* ── Recalculate result ────────────────────────────────────────────── */}
      <AnimatePresence>
        {recalcResult && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-start gap-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-emerald-800">Actuals Rebuilt Successfully</p>
                <div className="mt-1 flex flex-wrap gap-4 text-xs text-slate-500">
                  <span><strong className="text-slate-700">{recalcResult.channels_rebuilt}</strong> channels rebuilt</span>
                  <span><strong className="text-slate-700">{recalcResult.raw_rows_processed}</strong> raw rows processed</span>
                  <span><strong className="text-slate-700">{recalcResult.raw_rows_mapped}</strong> rows matched</span>
                  {recalcResult.unmapped_csv_categories?.length > 0 && (
                    <span className="text-amber-600 font-medium">
                      {recalcResult.unmapped_csv_categories.length} CSV categories still unmapped
                    </span>
                  )}
                </div>
              </div>
              <button onClick={() => setRecalcResult(null)} className="text-slate-400 hover:text-slate-600 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Two-panel mapping area ────────────────────────────────────────── */}
      {loading ? (
        <div className="grid lg:grid-cols-2 gap-4">
          {[0, 1].map(i => (
            <div key={i} className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
              <div className="h-4 w-36 bg-slate-200 rounded animate-pulse" />
              <div className="h-8 bg-slate-100 rounded-lg animate-pulse" />
              {[...Array(6)].map((_, j) => (
                <div key={j} className="h-10 bg-slate-100 rounded-lg animate-pulse"
                  style={{ animationDelay: `${j * 60}ms` }} />
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4 items-start">

          {/* LEFT — Unmapped ──────────────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-amber-100">
                  <Link2Off className="w-3.5 h-3.5 text-amber-600" />
                </div>
                <span className="text-sm font-semibold text-slate-800">Unmapped CSV Categories</span>
              </div>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                {unmappedCats.length} remaining
              </span>
            </div>

            {/* Search */}
            <div className="px-4 py-2.5 border-b border-slate-100">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input
                  placeholder="Search unmapped categories…"
                  value={searchUnmapped}
                  onChange={e => setSearchUnmapped(e.target.value)}
                  className="pl-8 h-8 text-sm bg-slate-50 border-slate-200 focus:bg-white"
                />
              </div>
            </div>

            {/* List */}
            <div className="p-3 space-y-1.5 max-h-[480px] overflow-y-auto">
              <AnimatePresence mode="popLayout">
                {filteredUnmapped.length === 0 ? (
                  <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="py-12 text-center">
                    <CheckCircle2 className="w-7 h-7 text-emerald-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-500 font-medium">
                      {searchUnmapped ? 'No matches' : 'All categories mapped!'}
                    </p>
                  </motion.div>
                ) : (
                  <motion.div variants={stagger} initial="hidden" animate="visible" className="space-y-1.5">
                    {filteredUnmapped.map((cat, i) => (
                      <UnmappedRow
                        key={cat} csvCategory={cat}
                        aopCategories={aopCategories}
                        pending={pendingMap[cat]}
                        onChange={val => setPendingMap(prev => ({ ...prev, [cat]: val }))}
                        delay={i}
                      />
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Pending save footer */}
            <AnimatePresence>
              {pendingCount > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }}
                  className="border-t border-primary-100 bg-primary-50 px-4 py-2.5 flex items-center justify-between"
                >
                  <span className="text-xs text-primary-700 font-medium">
                    {pendingCount} assignment{pendingCount > 1 ? 's' : ''} ready to save
                  </span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost"
                      onClick={() => setPendingMap({})}
                      className="h-7 text-xs text-slate-500 hover:text-slate-700">
                      Clear
                    </Button>
                    <Button size="sm" onClick={handleSave} disabled={saving}
                      className="h-7 text-xs bg-primary-500 hover:bg-primary-600 text-white gap-1.5">
                      {saving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                      {saving ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* RIGHT — Saved mappings ───────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-emerald-100">
                  <Link2 className="w-3.5 h-3.5 text-emerald-600" />
                </div>
                <span className="text-sm font-semibold text-slate-800">Saved Mappings</span>
              </div>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                {savedMappings.length} mapped
              </span>
            </div>

            {/* Column labels */}
            <div className="px-4 py-2 border-b border-slate-100 grid grid-cols-[1fr_auto_1fr] gap-3 items-center">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">CSV Category</span>
              <span />
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">AOP Category</span>
            </div>

            {/* Search */}
            <div className="px-4 py-2.5 border-b border-slate-100">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input
                  placeholder="Search mapped categories…"
                  value={searchMapped}
                  onChange={e => setSearchMapped(e.target.value)}
                  className="pl-8 h-8 text-sm bg-slate-50 border-slate-200 focus:bg-white"
                />
              </div>
            </div>

            {/* List */}
            <div className="p-3 space-y-1.5 max-h-[480px] overflow-y-auto">
              <AnimatePresence mode="popLayout">
                {filteredMapped.length === 0 ? (
                  <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="py-12 text-center">
                    <Link2Off className="w-7 h-7 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500 font-medium">
                      {searchMapped ? 'No matches' : 'No mappings yet'}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Assign categories on the left and click Save
                    </p>
                  </motion.div>
                ) : (
                  <motion.div variants={stagger} initial="hidden" animate="visible" className="space-y-1.5">
                    {filteredMapped.map((m, i) => (
                      <MappedRow
                        key={m.csv_category} mapping={m}
                        onDelete={() => handleDelete(m.csv_category)}
                        delay={i}
                      />
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Footer hint */}
            <div className="border-t border-slate-100 px-4 py-2.5 bg-slate-50">
              <p className="text-xs text-slate-400">
                Hover any row to remove the mapping. Changes take effect after Recalculate.
              </p>
            </div>
          </motion.div>
        </div>
      )}

      {/* ── AOP skeleton reference ────────────────────────────────────────── */}
      <AnimatePresence>
        {!loading && aopCategories.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.25 }}
            className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm"
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 rounded-lg bg-blue-100">
                <Layers className="w-3.5 h-3.5 text-blue-600" />
              </div>
              <span className="text-sm font-semibold text-slate-800">AOP Skeleton — All Categories</span>
              <span className="ml-auto text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                {aopCategories.length} total
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {aopCategories.map(cat => (
                <span key={cat}
                  className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600
                             border border-slate-200 hover:bg-slate-200 transition-colors cursor-default">
                  {cat}
                </span>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}
